<script setup>
import { useModal } from 'vue-final-modal';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { getFileIcon } from '~/common/utils/pretty-file-icons.util';
import DmsAddStampPopup from '~/dms/components/settings/dms-add-stamp-popup.vue';

defineEmits(['stampClicked']);

const {
  $services,
  $t,
  auth_store,
  route,
  router,
} = useCommonImports();

const is_fetching_data = ref(true);
const data = ref([]);
onMounted(async () => {
  const response = await $services.dms.get({
    attribute: 'stamps',
  });
  data.value = response.data.stamps;
  is_fetching_data.value = false;
});

const { open: openAddStampModal, close: closeAddStampModal, patchOptions: patchStampOptions } = useModal({
  component: DmsAddStampPopup,
});

function stampHandler(stamp) {
  patchStampOptions({
    attrs: {
      stamp,
      submit: async (form) => {
        try {
          const payload = {
            name: form.name,
            file_name: form.file.sanitized_name || stamp?.file_name,
            file_size: form.file.file_size || stamp?.file_size,
            file_type: form.file.mime_type || stamp?.file_type,
            service: form.file.service_object || stamp?.service,
            asset: route.params.asset_id,
            organization: auth_store.current_organization?.uid,
          };
          let response;
          if (stamp) {
            response = await $services.dms.patch({
              attribute: `stamps/${stamp.uid}`,
              body: { stamps: [payload] },
            });
            const index = data.value.findIndex(item => item.uid === stamp.uid);
            data.value[index] = response.data.stamps[0];
            data.value = [...data.value];
          }
          else {
            response = await $services.dms.post({
              attribute: 'stamps',
              body: { stamps: [payload] },
            });
            data.value.push(response.data.stamps[0]);
            data.value = [...data.value];
          }
          closeAddStampModal();
        }
        catch (e) {
          console.error(e);
        }
      },
      onClose() {
        closeAddStampModal();
      },
    },
  });
  openAddStampModal();
}

function getPlaceholderIcon(stamp) {
  return new URL(`/assets/file-type-icons/${getFileIcon(stamp.file_name)}.svg`, import.meta.url).href;
}
</script>

<template>
  <hawk-menu
    additional_trigger_classes="!ring-0"
  >
    <template #trigger>
      <slot name="trigger">
        <!-- Default trigger if no custom trigger provided -->
        <div class="flex items-center text-sm font-semibold">
          <IconHawkStampTwo class="w-5 h-5" />
        </div>
      </slot>
    </template>
    <template #content="{ close }">
      <div class="w-[400px]  rounded-lg">
        <div class="p-2 flex items-center border-b">
          <div class="flex-1 text-sm font-semibold">
            {{ $t('Add stamp') }}
          </div>
          <div>
            <hawk-button icon type="text" @click="stampHandler()">
              <IconHawkPlus class="w-4 h-4" />
            </hawk-button>
            <hawk-button icon type="text" @click="router.push({ name: 'dms-stamp-settings' })">
              <IconHawkSettingsOne class="w-4 h-4" />
            </hawk-button>
          </div>
        </div>
        <div class="grid grid-cols-3 gap-2 p-2 overflow-y-auto max-h-[400px] scrollbar">
          <div v-if="data.length === 0" class="flex flex-col items-center justify-center col-span-3 p-3">
            <div class="rounded-full bg-gray-100 p-3">
              <IconHawkStampTwo class="w-5 h-5 stroke-gray-600" />
            </div>
            <div class="mt-3 text-sm font-medium">
              {{ $t('No stamps added') }}
            </div>
            <div class="mt-3">
              <hawk-button type="outlined" @click="stampHandler()">
                <IconHawkPlus class="w-4 h-4" />
                {{ $t('Add stamp') }}
              </hawk-button>
            </div>
          </div>
          <div v-for="stamp in data" :key="stamp.uid" v-tippy="{ content: stamp.name }" class="flex items-center gap-2 border rounded-md overflow-hidden cursor-pointer" @click="close(); $emit('stampClicked', stamp)">
            <img :src="stamp.thumbnail_small || getPlaceholderIcon(stamp)" alt="" class="w-[120px] h-[120px]">
          </div>
        </div>
      </div>
    </template>
  </hawk-menu>
</template>
